import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Storage, ref, uploadBytes } from '@angular/fire/storage';
import { Observable, from, map, switchMap } from 'rxjs';

import { MAX_FILE_SIZE_MB, UPLOAD_STATUS, UPLOAD_TYPE } from 'src/app/constants';

import { Upload } from '../models';
import { ApiFile } from '../models/file.model';

import { AuthService } from './auth.service';
import { UsersService } from './users.service';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  constructor(
    private afst: Storage,
    private authService: AuthService,
    private usersService: UsersService,
    private http: HttpClient,
  ) {}

  // Handle image uploads for the angular-editor wysiwyg.
  uploadWysiwygFile(file: File) {
    const upload = new Upload(
      new File([file], crypto.randomUUID(), {
        type: file.type,
        lastModified: file.lastModified,
      }),
    );
    const finishedUpload$ = from(this.uploadFileAsPromise(upload, `wysiwyg_images`));

    return finishedUpload$.pipe(
      map((finishedUpload) => {
        return new HttpResponse({
          body: {
            imageUrl: finishedUpload.url,
          },
        });
      }),
    );
  }

  // return a promise which upload file & get download URL
  async uploadFileAsPromise(upload: Upload, basePath: string, nameOverride?: string): Promise<Upload> {
    const name = nameOverride ?? upload.file.name;
    const storageRef = ref(this.afst, `${this.authService.tenantId}/${basePath}/${name}`);
    const arrayBuffer = await upload.file.arrayBuffer();
    const contentType = upload.file.type;

    const snapshot = await uploadBytes(storageRef, arrayBuffer, {
      customMetadata: upload.metadata || {},
      contentType: contentType,
    });

    const bucket = snapshot.ref.bucket;
    upload.path = snapshot.ref.fullPath;

    const encodedPath = encodeURIComponent(`${upload.path}`);
    const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
    upload.url = url;

    upload.name = name;

    return upload;
  }

  async upload(uploads: Upload[], basePath: string = 'plan_headers', nameOverride?: string) {
    return Promise.all(
      uploads.map((item) => {
        return this.uploadFileAsPromise(item, basePath, nameOverride);
      }),
    );
  }

  async uploadProfilePicture(userId: string, file: File) {
    if (file.size / 1000 / 1000 > MAX_FILE_SIZE_MB) {
      throw new Error('Files should not be larger than 5mb; try something smaller.');
    }

    const currentUpload = new Upload(file);

    currentUpload.metadata = {
      uploadId: crypto.randomUUID(),
      tenantId: this.authService.tenantId,
      createdBy: userId,
      filename: file.name,
      status: UPLOAD_STATUS.NONE,
      type: UPLOAD_TYPE.PROFILE_PIC,
      objectId: userId,
    };

    await this.upload([currentUpload], `profiles/${userId}`);
    await this.usersService.updateUser(userId, {
      photo: currentUpload.url,
      photoPath: currentUpload.path,
    });

    this.authService.profile.photo = currentUpload.url;
    this.authService.profile.photoPath = currentUpload.path;
  }

  async uploadSupportLogo(file: File) {
    if (file.size / 1000 / 1000 > MAX_FILE_SIZE_MB) {
      throw new Error('Files should not be larger than 5mb; try something smaller.');
    }

    const currentUpload = new Upload(file);

    currentUpload.metadata = {
      uploadId: crypto.randomUUID(),
      tenantId: this.authService.tenantId,
      createdBy: this.authService.profile.id,
      filename: file.name,
      status: UPLOAD_STATUS.NONE,
      type: UPLOAD_TYPE.PROFILE_PIC,
      objectId: this.authService.tenantId,
    };

    await this.upload([currentUpload], `support-logos/${this.authService.tenantId}`);

    this.authService.tenant.supportLogo = currentUpload.url;
  }

  /**
   * Uploads one or more files for a slack conversation.
   * @param files An array of File objects to upload.
   * @param conversationId Optional ID of the conversation to attach the files to the initial message.
   * @returns An observable that emits an array of ApiFile objects representing the created files.
   */
  uploadFilesForSlackConversation(files: File[], conversationId?: string): Observable<ApiFile[]> {
    const formData = new FormData();

    files.forEach((file, index) => {
      // Use a generic name like 'file0', 'file1', etc., as the backend handler uses .any()
      formData.append(`file${index}`, file, file.name);
    });

    // Add conversationId to the form data if provided
    if (conversationId) {
      formData.append('conversationId', conversationId);
    }

    // Get fresh auth token (handles refresh automatically)
    return from(
      (async () => {
        const user = this.authService.user;
        if (!user) {
          throw new Error('User not authenticated');
        }

        const token = await user.getIdToken();

        // Create authorization header
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
          // Note: Content-Type is typically set automatically by the browser for FormData
        });

        return { headers };
      })(),
    ).pipe(
      switchMap(({ headers }) => {
        // Make the actual HTTP request with fresh token
        return this.http.post<ApiFile[]>('/api/files', formData, {
          headers: headers,
          // No need for reportProgress or observe events unless you want upload progress
        });
      }),
    );
  }
}
